import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  TablePagination,
  Skeleton,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  Chip,
  MenuItem,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PageLayout from '../../components/layout/PageLayout';
import { useGetCallRecordsQuery, useDeleteCallRecordMutation } from '../../store/api';
import { extractList, extractPagination } from '../../utils/response';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import {
  CALL_RECORDS_CREATE_PATH,
  CALL_RECORDS_VIEW_PATH,
  CALL_RECORDS_EDIT_PATH,
} from '../../navigations/path';
import SweetAlert from '../../utils/sweetAlert';
import Can from '../../components/permissions/Can';
import {
  getCallRecordId,
  getLeadName,
  getLeadAssigneeNames,
  getStatusLabel,
  getRecordingsList,
  getRecordingUrl,
  getRecordingDisplayName,
} from './callRecordHelpers';

const CallRecordList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuItem, setActionMenuItem] = useState(null);

  const queryParams = useMemo(() => {
    const params = {
      // `author` is not rendered on this table
      'relations[]': ['lead.assignees', 'recordings', 'status'],
      paginate: 1,
      page: page + 1,
      per_page: rowsPerPage,
    };
    if (search) params.search = search;
    return params;
  }, [page, rowsPerPage, search]);

  const { data, isFetching: loading, error } = useGetCallRecordsQuery(queryParams);
  const [deleteCallRecord] = useDeleteCallRecordMutation();

  const list = useMemo(() => {
    const rawList = data?.data?.list ?? data?.list ?? extractList(data);
    return Array.isArray(rawList) ? rawList : [];
  }, [data]);

  const total = useMemo(() => {
    const pag = data?.data?.pagination ?? data?.pagination ?? extractPagination(data);
    return pag.total ?? 0;
  }, [data]);

  useEffect(() => {
    if (error) toast.error(error?.data?.message || 'Failed to load call recordings');
  }, [error]);

  const handleSearch = () => setSearch(searchInput);
  const handlePageChange = (_, newPage) => setPage(newPage);
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleView = (item) => {
    const id = getCallRecordId(item);
    if (id) navigate(`${CALL_RECORDS_VIEW_PATH}/${id}`);
  };

  const handleEdit = (item) => {
    const id = getCallRecordId(item);
    if (id) navigate(`${CALL_RECORDS_EDIT_PATH}/${id}`);
  };

  const handleDelete = async (item) => {
    const id = getCallRecordId(item);
    if (!id) return;
    const result = await SweetAlert.deleteConfirm('this recording');
    if (!result?.isConfirmed) return;
    setDeletingId(id);
    try {
      await deleteCallRecord(id).unwrap();
      toast.success('Recording deleted.');
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to delete recording');
    } finally {
      setDeletingId(null);
    }
  };

  const openActionMenu = (event, item) => {
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
    setActionMenuItem(item);
  };

  const closeActionMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuItem(null);
  };

  const handleMenuView = () => {
    if (actionMenuItem) handleView(actionMenuItem);
    closeActionMenu();
  };

  const handleMenuEdit = () => {
    if (actionMenuItem) handleEdit(actionMenuItem);
    closeActionMenu();
  };

  const handleMenuDelete = () => {
    if (actionMenuItem) handleDelete(actionMenuItem);
    closeActionMenu();
  };

  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5" fontWeight={600}>
            Call Recordings ({total})
          </Typography>
          <Can permission="call-record-create">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate(CALL_RECORDS_CREATE_PATH)}
            >
              Upload recording
            </Button>
          </Can>
        </Box>

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              sx={{ minWidth: 260 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <Button variant="outlined" onClick={handleSearch}>
              Search
            </Button>
            <Button
              variant="text"
              onClick={() => {
                setSearchInput('');
                setSearch('');
                setPage(0);
              }}
            >
              Clear
            </Button>
          </Box>
        </Paper>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Lead name</TableCell>
                <TableCell>Assignees</TableCell>
                <TableCell>Recordings</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center" sx={{ width: 120 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(rowsPerPage)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={5}>
                      <Skeleton height={40} />
                    </TableCell>
                  </TableRow>
                ))
              ) : list.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                    No recordings found.
                  </TableCell>
                </TableRow>
              ) : (
                list.map((item) => {
                  const id = getCallRecordId(item);
                  const isDeleting = deletingId === id;
                  const recordings = getRecordingsList(item);
                  return (
                    <TableRow key={id}>
                      <TableCell>{getLeadName(item)}</TableCell>
                      <TableCell sx={{ maxWidth: 280 }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                          {getLeadAssigneeNames(item)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 260, maxWidth: 440 }}>
                        {recordings.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        ) : (
                          <Stack spacing={1.5} sx={{ alignItems: 'flex-start', py: 0.5 }}>
                            {recordings.map((rec, idx) => {
                              const recUrl = getRecordingUrl(rec);
                              const label = getRecordingDisplayName(rec);
                              const rk = rec?.id ?? rec?.uuid ?? `${id}-${idx}`;
                              return (
                                <Box
                                  key={rk}
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    gap: 0.75,
                                    width: '100%',
                                    pb: idx < recordings.length - 1 ? 1.5 : 0,
                                    borderBottom:
                                      idx < recordings.length - 1 ? '1px solid' : 'none',
                                    borderColor: 'divider',
                                  }}
                                >
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {recordings.length > 1 ? `${idx + 1}. ${label}` : label}
                                  </Typography>
                                  {recUrl ? (
                                    <audio
                                      controls
                                      preload="none"
                                      src={recUrl}
                                      style={{ width: '100%', maxWidth: 320, verticalAlign: 'middle' }}
                                    >
                                      Your browser does not support audio.
                                    </audio>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      No URL
                                    </Typography>
                                  )}
                                </Box>
                              );
                            })}
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={getStatusLabel(item)} variant="outlined" />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          aria-label="Actions"
                          disabled={isDeleting}
                          onClick={(e) => openActionMenu(e, item)}
                          sx={{ color: 'text.secondary' }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Menu
          anchorEl={actionMenuAnchor}
          open={Boolean(actionMenuAnchor)}
          onClose={closeActionMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { minWidth: 140 } }}
        >
          <Can permission="call-record-view">
            <MenuItem onClick={handleMenuView}>
              <ListItemIcon>
                <VisibilityIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>View</ListItemText>
            </MenuItem>
          </Can>
          <Can permission="call-record-edit">
            <MenuItem onClick={handleMenuEdit}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
          </Can>
          <Can permission="call-record-delete">
            <MenuItem onClick={handleMenuDelete} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          </Can>
        </Menu>

        {!loading && list.length > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Rows per page"
          />
        )}
      </Container>
    </PageLayout>
  );
};

export default CallRecordList;
